import React, { useEffect, useState } from 'react';
import {
  subscribe,
  getDeferredPrompt,
  promptInstall,
  isStandalone,
  getPlatform,
} from '../utils/pwaInstall';

const INSTRUCTIONS = {
  ios: {
    title: 'Install on iPhone / iPad',
    steps: [
      <>Tap the <b>Share</b> button <ShareIcon /> (bottom bar on iPhone, top bar on iPad; in Chrome it is in the address bar).</>,
      <>Scroll down and tap <b>Add to Home Screen</b>.</>,
      <>Tap <b>Add</b> in the top-right corner.</>,
    ],
    note: 'On iOS older than 16.4, this only works from Safari.',
  },
  android: {
    title: 'Install on Android',
    steps: [
      <>Open the browser menu <b>⋮</b> (top-right).</>,
      <>Tap <b>Install app</b> or <b>Add to Home screen</b>.</>,
      <>Confirm by tapping <b>Install</b>.</>,
    ],
  },
  'mac-safari': {
    title: 'Install on Mac',
    steps: [
      <>In the menu bar, open <b>File</b> (or the <b>Share</b> button <ShareIcon />).</>,
      <>Choose <b>Add to Dock…</b></>,
      <>Click <b>Add</b>.</>,
    ],
    note: 'Requires Safari 17 (macOS Sonoma) or newer.',
  },
};

function ShareIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="inline-block w-4 h-4 align-text-bottom text-blue-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 11v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
    </svg>
  );
}

const InstallPWAButton = () => {
  const [hasPrompt, setHasPrompt] = useState(() => !!getDeferredPrompt());
  const [standalone, setStandalone] = useState(() => isStandalone());
  const [showHelp, setShowHelp] = useState(false);
  const platform = getPlatform();

  useEffect(() => {
    const update = () => {
      setHasPrompt(!!getDeferredPrompt());
      setStandalone(isStandalone());
    };
    const unsubscribe = subscribe(update);
    const mq = window.matchMedia?.('(display-mode: standalone)');
    mq?.addEventListener?.('change', update);
    update();
    return () => {
      unsubscribe();
      mq?.removeEventListener?.('change', update);
    };
  }, []);

  // Already running as an installed app: nothing to show.
  if (standalone) return null;

  // Native prompt available (Chrome / Edge / Samsung Internet / Opera).
  // Otherwise fall back to manual instructions where installing is possible.
  const instructions = !hasPrompt ? INSTRUCTIONS[platform] : null;
  if (!hasPrompt && !instructions) return null;

  const handleInstallClick = async () => {
    if (hasPrompt) {
      const outcome = await promptInstall();
      console.log(`User ${outcome} the install prompt`);
    } else {
      setShowHelp(true);
    }
  };

  return (
    <>
      <button
        className="w-full  bg-orange-400  text-white p-4 max-[450px]:p-2 sm:p-2 md:p-2 rounded-full shadow-lg my-2"
        onClick={handleInstallClick}
      >
        Install App
      </button>

      {showHelp && instructions && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowHelp(false)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-5 text-gray-800"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-install-title"
          >
            <div className="flex items-center gap-3 mb-4">
              <img src="/192.png" alt="" className="w-12 h-12 rounded-xl" />
              <h2 id="pwa-install-title" className="text-lg font-semibold">
                {instructions.title}
              </h2>
            </div>
            <ol className="list-decimal pl-5 space-y-2 text-sm">
              {instructions.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {instructions.note && (
              <p className="mt-3 text-xs text-gray-500">{instructions.note}</p>
            )}
            <button
              className="mt-5 w-full bg-orange-400 text-white py-2 rounded-full"
              onClick={() => setShowHelp(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWAButton;
